// File: Home.js
import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./Home.css";

// FontAwesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faClock,
    faBugSlash,
    faMoneyBillWave,
    faChartPie,
} from "@fortawesome/free-solid-svg-icons";

const features = [
    { id: 1, title: "User-Friendly Interface", icon: "Computer" },
    { id: 2, title: "Automated Bank Transaction Feeds", icon: "Bank" },
    { id: 3, title: "Cash Flow Forecasting", icon: "Chart" },
    { id: 4, title: "Budgeting and Forecasting", icon: "Report" },
    { id: 5, title: "Expense Approval Workflow", icon: "Check" },
    { id: 6, title: "Mobile Access", icon: "Phone" },
    { id: 7, title: "Customizable Reporting", icon: "Pie" },
    { id: 8, title: "Document Management", icon: "Folder" },
];

const keyFeatures = [
    {
        id: 1,
        title: "Unified Product Management",
        desc: "Easily manage your entire inventory with centralized product data, ensuring accuracy across all sales channels.",
        image: "/images/features/unified.png",
    },
    {
        id: 2,
        title: "Multiple Price Labels",
        desc: "Print and manage multiple price labels with ease for better retail flexibility and customer clarity.",
        image: "/images/features/price-labels.png",
    },
    {
        id: 3,
        title: "Multi Barcode Support",
        desc: "Handle multiple barcodes per product to streamline billing and stock management efficiently.",
        image: "/images/features/multi-barcode.png",
    },
    {
        id: 4,
        title: "WhatsApp Integration",
        desc: "Send invoices, payment reminders, and reports directly through WhatsApp for instant communication.",
        image: "/images/features/whatsapp.png",
    },
    {
        id: 5,
        title: "Multi-Branch Management",
        desc: "Seamlessly manage stock, sales, and reports across multiple branches in real-time.",
        image: "/images/features/multibranch.png",
    },
    {
        id: 6,
        title: "Cloud Management",
        desc: "Access your business data anytime, anywhere with secure cloud backup and management.",
        image: "/images/features/cloud.png",
    },
    {
        id: 7,
        title: "POS System",
        desc: "Modern POS with touch support, barcode scanning, and product search for quick billing.",
        image: "/images/features/pos.png",
    },
    {
        id: 8,
        title: "Real-Time Analysis",
        desc: "Track business performance with real-time dashboards and advanced analytics.",
        image: "/images/features/realtime-analysis.png",
    },
    {
        id: 9,
        title: "Mobile Connectivity",
        desc: "View live sales, reports, and inventory from mobile devices with real-time networking.",
        image: "/images/features/mobile.png",
    },
];
const benefits = [
    {
        id: 1,
        title: "Save Time with Automation",
        desc: "Automated invoicing, expense tracking, and reporting so you can focus on growing your business.",
        icon: faClock,
    },
    {
        id: 2,
        title: "Reduce Errors in Bookkeeping",
        desc: "Smart tools minimize human errors and ensure your financial data is always accurate.",
        icon: faBugSlash,
    },
    {
        id: 3,
        title: "Get Paid Faster",
        desc: "Send invoices instantly and accept online payments to improve cash flow.",
        icon: faMoneyBillWave,
    },
    {
        id: 4,
        title: "Make Smarter Decisions",
        desc: "Real-time dashboards and analytics help you track performance and plan better.",
        icon: faChartPie,
    },
];

const screenshots = [
    { id: 1, title: "Dashboard", image: "/images/screenshots/dashboard.png" },
    { id: 2, title: "Invoice Creation", image: "/images/screenshots/invoice.png" },
    { id: 3, title: "Expense Tracking", image: "/images/screenshots/expenses.png" },
    { id: 4, title: "Report Generation", image: "/images/screenshots/reports.png" },
];
export default function Home() {
    const slides = [
        "/images/slider1.png",
        "/images/slider2.png",
        "/images/slider3.png",
        "/images/slider4.png",
        "/images/slider6.png",
        "/images/slider7.png",
    ];

    return (
        <div className="home-wrapper">
            <main className="content container">
                <section className="left">
                    <h1 className="title home-title world-best-title">
                        World Class Accounting Software
                    </h1>

                

                    <div className="grid">
                        {features.map((f) => (
                            <article key={f.id} className="card">
                                <div className="icon">
                                    {/* Old Custom SVG Icons */}
                                    <Icon name={f.icon} />
                                </div>
                                <div className="meta">
                                    <h3>{f.title}</h3>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                {/* Right Section - Slider */}
                <aside className="right">
                    <Swiper
                        modules={[Autoplay]}
                        spaceBetween={30}
                        slidesPerView={1}
                        autoplay={{ delay: 3000, disableOnInteraction: false }}
                        className="hero-slider"
                    >
                        {slides.map((src, index) => (
                            <SwiperSlide key={index}>
                                <img src={src} alt={`slide-${index}`} className="slider-image" />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </aside>
            </main>
            <h2 className="section-title">Explore Our Key Features</h2>

            <section className="key-features container">

                <div className="features-list">
                    {keyFeatures.map((f, index) => (
                        <div
                            key={f.id}
                            className={`feature-item_home ${index % 2 === 0 ? "left" : "right"}`}
                        >
                            <div className="feature-image">
                                <img src={f.image} alt={f.title} />
                            </div>
                            <div className="feature-content">
                                <h3>{f.title}</h3>
                                <p>{f.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
            <section className="benefits-section">
                <div className="container benefits-layout">
                    {/* Left: Title + description */}
                    <div className="benefits-info">
                        <h2 className="section-title">Why Choose Our Software?</h2>
                        <p className="section-subtitle">
                            Our accounting platform isn’t just about numbers — it’s about
                            helping your business save time, stay accurate, and grow faster with
                            smart tools you can trust.
                        </p>
                    </div>

                    {/* Right: Grid of benefits */}
                    <div className="benefits-grid">
                        {benefits.map((b) => (
                            <div key={b.id} className="benefit-card">
                                <FontAwesomeIcon icon={b.icon} className="benefit-icon" />
                                <div>
                                    <h3>{b.title}</h3>
                                    <p>{b.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            <section className="screenshots-section">
                {/* Animated bubble background layer and animated images */}
                <div className="bubble-bg">
                    <div className="bubble b1"></div>
                    <div className="bubble b2"></div>
                    <div className="bubble b3"></div>
                    <div className="bubble b4"></div>
                    <img src="/images/audit.jpg" alt="audit" className="animated-img img1" />
                    <img src="/images/audit.jpg" alt="audit" className="animated-img img2" />
                </div>
                {/* Heading */}
                <h2 className="section-title">See Our Software in Action</h2>
                {/* Subtitle */}
                <p className="section-subtitle">
                    Explore how our accounting software simplifies your workflow with a clean and intuitive design.
                </p>
                {/* Screenshot Grid */}
                <div className="screenshots-grid">
                    {screenshots.map((s) => (
                        <div key={s.id} className="screenshot-card">
                            <img src={s.image} alt={s.title} />
                            <h3>{s.title}</h3>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}

function Icon({ name }) {
    switch (name) {
        case "Computer":
            return (
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="4" width="18" height="12" rx="2" stroke="#1f3652" strokeWidth="1.5" />
                    <rect x="6" y="8" width="12" height="4" rx="1" fill="#eaf3fb" />
                    <path d="M2 20h20" stroke="#1f3652" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
            );
        case "Bank":
            return (
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                    <path d="M12 3l8 4v2H4V7l8-4z" stroke="#1f3652" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                    <rect x="6" y="11" width="12" height="6" rx="1" stroke="#1f3652" strokeWidth="1.2" />
                    <path d="M10 14v2" stroke="#1f3652" strokeWidth="1.2" />
                    <path d="M14 14v2" stroke="#1f3652" strokeWidth="1.2" />
                </svg>
            );
        case "Chart":
            return (
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="6" width="18" height="12" rx="2" stroke="#1f3652" strokeWidth="1.3" />
                    <path d="M7 14V10" stroke="#1f3652" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M11 14v-4" stroke="#1f3652" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M15 14v-2" stroke="#1f3652" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
            );
        case "Report":
            return (
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="4" width="14" height="16" rx="2" stroke="#1f3652" strokeWidth="1.3" />
                    <path d="M18 7v10" stroke="#1f3652" strokeWidth="1.3" />
                    <path d="M7 9h6" stroke="#1f3652" strokeWidth="1.2" strokeLinecap="round" />
                    <path d="M7 12h6" stroke="#1f3652" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
            );
        case "Check":
            return (
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="#1f3652" strokeWidth="1.2" />
                    <path d="M8.5 12.5l2 2 4-4" stroke="#1f3652" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            );
        case "Phone":
            return (
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                    <rect x="6" y="3" width="12" height="18" rx="2" stroke="#1f3652" strokeWidth="1.3" />
                    <circle cx="12" cy="18" r="0.8" fill="#1f3652" />
                </svg>
            );
        case "Pie":
            return (
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="#1f3652" strokeWidth="1.3" />
                    <path d="M12 12V3a9 9 0 0 1 6.36 15.36L12 12z" fill="#eaf3fb" stroke="#1f3652" strokeWidth="0.6" />
                </svg>
            );
        case "Folder":
            return (
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                    <path d="M3 7a2 2 0 0 1 2-2h3l2 2h7a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="#1f3652" strokeWidth="1.2" />
                </svg>
            );
        default:
            return null;
    }
}
